import { AxiosResponse } from "axios";
import BaseAPI from "../api";

/**
 * เมนู "เรียกคืนแผงค้า" — ตรงตาม stall-reclaim-api.md
 *
 * backend ใช้ตาราง deposit_refund* ชุดเดียวกับเมนูคืนค่ามัดจำแผง แยกด้วยคอลัมน์ request_type
 * จึงคืน field ชื่อเดิมทั้งชุด (id_deposit_refund, deposit_refund_no, request_*)
 *
 * ไฟล์นี้ประกาศ type ของตัวเองครบทุกตัว **ไม่ extends/alias ของ deposit-refund**
 * เพราะฝั่งนั้นมี field ที่โมดูลนี้ไม่มี (refund_method, refund_method_name) ถ้าลากมาใช้
 * TypeScript จะบอกว่ามี field ที่ runtime เป็น undefined
 */
const path = "stall-reclaim";

/** envelope มาตรฐาน — ฝั่งหน้าจออ่าน res.data.data ไม่ใช่ res.data */
export type BaseResponse<T> = {
  success: boolean;
  status: number;
  message: string;
  data: T;
};

/** nestjs-typeorm-paginate — total อยู่ใน meta ไม่ใช่ header 'total' แบบ API เก่า */
export type Paginated<T> = {
  items: T[];
  meta: {
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
  };
  links: { first: string; previous: string; next: string; last: string };
};

/**
 * ยอดเงินที่ backend คืนเป็น DECIMAL ของ MySQL → มาเป็น string
 * ทุกจุดที่ใช้ต้อง Number() ก่อนเสมอ (ใช้ toAmount ที่มีอยู่แล้ว)
 */
export type DecimalString = string | number;

/** DELETED ไม่เคยคืนมาใน API (soft delete) จึงไม่อยู่ใน union */
export type StallReclaimStatus = "WAITING" | "APPROVE" | "NOT_APPROVE";

/** ไม่มี CANCEL เพราะไม่มีฝั่งลูกค้าให้ยกเลิกคำสั่ง */
export type StallReclaimLogAction = "CREATE" | "REVIEW" | "DELETE";

export type StallReclaimDeductType = "MOVE_OUT_PENALTY" | "DAMAGE_INSURANCE";

export type LeaseStatus = "ACTIVE" | "INACTIVE" | "CANCEL" | "TRANSFER";

export interface StallReclaimParams {
  keyword?: string;
  status?: StallReclaimStatus;
  /**
   * สาขาที่เลือกไว้ (getBranch()) — ต้องส่งทุกเส้น
   * backend ไม่ได้ scope สาขาจาก token เพราะ user 1 คนไม่ผูกกับสาขาเดียว
   */
  branchId?: number;
  zoneId?: number;
  stallId?: number;
  stallTypeId?: number;
  /** YYYY-MM-DD — กรองที่วันเวลาที่สร้างคำสั่ง (request_date) */
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

/** แถวในตารางรายการคำสั่ง */
export interface StallReclaimRow {
  id_deposit_refund: number;
  /** 'RCL-2569-0001' */
  deposit_refund_no: string;
  leaseAgreementId: number;
  lease_agreement_no: string | null;
  userId: number;
  customerId: number;
  tenant_name: string | null;
  zoneId: number | null;
  zone_name: string | null;
  stallId: number | null;
  stall_name: string | null;
  stall_number: string | null;
  stallTypeId: number | null;
  stall_type_name: string | null;
  /** เงินมัดจำรวมของสัญญา = ยอดตั้งต้นที่จะคำนวณคืนตอนพิจารณา */
  request_amount: DecimalString;
  /** null = ยังไม่พิจารณา หรือไม่อนุมัติ */
  approved_amount: DecimalString | null;
  /** รายละเอียดคำสั่ง (คอลัมน์ "รายละเอียด" ในตาราง) */
  request_note: string | null;
  request_date: string | null;
  /** กำหนดคืนพื้นที่ — DATE ที่ serialize เป็น UTC ISO ต้อง format ด้วย timezone Asia/Bangkok */
  due_date: string | null;
  reviewed_date: string | null;
  reviewed_id: number | null;
  reviewed_name: string | null;
  /** วันที่ระบบยกเลิกสัญญาให้ตอนอนุมัติ (null เมื่อยังไม่อนุมัติ) */
  lease_canceled_date: string | null;
  status: StallReclaimStatus;
}

export interface StallReclaimSummary {
  all: number;
  WAITING: number;
  APPROVE: number;
  NOT_APPROVE: number;
}

/** 1 บรรทัดใน "รายละเอียดการคำนวณเงินคืน" — shape เดียวกับฝั่งคืนค่ามัดจำ */
export interface StallReclaimDeduct {
  id_deposit_refund_deduct?: number;
  seq?: number;
  deduct_type: StallReclaimDeductType;
  /** snapshot ตอนพิจารณา เอาไปโชว์ตรงๆ ได้ */
  label: string;
  base_amount: DecimalString;
  deduct_amount: DecimalString;
  remain_amount: DecimalString;
}

/**
 * รายละเอียดคำสั่ง (modal รายละเอียด)
 * Omit status ออกจาก Row แล้วประกาศใหม่เพื่อให้อ่านง่ายว่าเส้นนี้มีอะไรเพิ่มจากตาราง
 */
export interface StallReclaimDetail extends Omit<StallReclaimRow, "status"> {
  branchId: number | null;
  tenant_idCard: string | null;
  tenant_mobilePhone: string | null;
  /** วันที่หมดสัญญาเช่า */
  lease_date_end: string | null;
  lease_status: LeaseStatus | null;
  lease_status_name: string | null;
  /** ยอดตั้งต้นของ 2 บรรทัดที่หักได้ — modal พิจารณาต้องใช้ค่านี้ */
  move_out_penalty_rate: number;
  damage_insurance_limit: number;
  /** เจ้าหน้าที่ที่ออกคำสั่ง */
  request_by_id: number | null;
  request_by_name: string | null;
  review_note: string | null;
  /** DATE serialize เป็น UTC ISO */
  refund_date: string | null;
  doc_ref: string | null;
  status: StallReclaimStatus;
  /** [] ถ้ายังไม่พิจารณา หรือไม่อนุมัติ */
  deducts: StallReclaimDeduct[];
}

export interface StallReclaimFile {
  id_deposit_refund_file: number;
  /** '1788864174238-ใบตรวจรับคืนพื้นที่.pdf' — timestamp นำหน้ากันชื่อไฟล์ชนกัน */
  file_name: string;
  mimetype: string | null;
  /** string (BIGINT) หน่วย byte */
  file_size: DecimalString | null;
  /** '/stall-reclaim/1/file/1/download' — ต่อกับ baseURL ได้เลย (ไม่คืน static path ตาม PDPA) */
  download_url: string;
}

/** ประวัติการพิจารณา เรียงใหม่ -> เก่ามาแล้ว */
export interface StallReclaimLog {
  id_deposit_refund_log: number;
  id_deposit_refund: number;
  action: StallReclaimLogAction;
  /** null เมื่อ action ไม่ใช่ REVIEW */
  result: "APPROVE" | "NOT_APPROVE" | null;
  note: string | null;
  approved_amount?: DecimalString | null;
  /** ใน log คืนเป็น 'YYYY-MM-DD' */
  refund_date?: string | null;
  doc_ref?: string | null;
  action_id: number | null;
  action_name: string | null;
  action_date: string | null;
  /** มีค่าเฉพาะ log ที่ action = REVIEW และ result = APPROVE */
  deducts: StallReclaimDeduct[];
  files: StallReclaimFile[];
}

/** ผลค้นหาผู้เช่าแผงค้า (ช่อง "ผู้เช่าแผงค้า" ใน modal สร้างคำสั่ง) */
export interface StallReclaimOwner {
  /** ใช้เรียก ownerLease() และส่งเป็น userId_owner ตอนสร้างคำสั่ง */
  userId: number;
  customerId: number;
  owner_fullname: string;
  /** backend mask มาแล้ว ('577xxxxxxx720') ห้าม mask ซ้ำ */
  idCard: string | null;
  mobilePhone: string | null;
  /** string (COUNT ของ MySQL) */
  lease_count: DecimalString;
}

/** สัญญาเช่าที่เรียกคืนได้ — เฉพาะสถานะ ACTIVE ("สัญญาเช่า") */
export interface StallReclaimLease {
  leaseAgreementId: number;
  branchId: number | null;
  userId: number;
  lease_agreement_no: string | null;
  tenant_name: string | null;
  stallId: number | null;
  zoneId: number | null;
  zone_name: string | null;
  stall_name: string | null;
  stall_number: string | null;
  stallTypeId: number | null;
  stall_type_name: string | null;
  /** DATE ที่ serialize เป็น UTC ISO ต้อง format ด้วย timezone Asia/Bangkok */
  date_start: string | null;
  date_end: string | null;
  lease_status: LeaseStatus;
  /** backend คืนคำไทยมาให้แล้ว ใช้แสดงตรงๆ ไม่ต้อง map */
  lease_status_name: string | null;
  move_out_penalty_rate: number;
  damage_insurance_limit: number;
  deposit_amount: number;
  /** เลขที่คำสั่งที่กำลังยึดสิทธิสัญญานี้อยู่ */
  blocked_by_no: string | null;
  blocked_by_status: StallReclaimStatus | null;
  /** ข้อความไทยพร้อมใช้ ใส่ tooltip ได้ตรงๆ (null เมื่อ can_reclaim = true) */
  blocked_reason: string | null;
  /** false = ออกคำสั่งกับสัญญานี้ไม่ได้ ต้อง disable แถว */
  can_reclaim: boolean;
}

export interface StallReclaimOwnerSearchParams {
  keyword?: string;
  branchId?: number;
  /** default 1 = เฉพาะคนที่มีสัญญาเช่าที่เรียกคืนได้ */
  only_has_active_lease?: 0 | 1;
  page?: number;
  limit?: number;
}

/**
 * body ของ POST /stall-reclaim
 * deposit_refund_no ไม่ส่ง = ระบบออกเลขให้ (ส่งมาเองเมื่อใช้เลขจากเอกสารจริง)
 */
export interface CreateStallReclaimBody {
  userId_owner: number;
  leaseAgreementIds: number[];
  deposit_refund_no?: string;
  /** กำหนดวันคืนพื้นที่ 'YYYY-MM-DD' */
  due_date?: string;
  reason?: string;
}

export interface CreateStallReclaimItem {
  id_deposit_refund: number;
  deposit_refund_no: string;
  leaseAgreementId: number;
  lease_agreement_no: string | null;
  request_amount: DecimalString;
}

/** 201 ถึงจะมีบางใบถูกข้าม ต้องอ่าน skipped[] มาแจ้งผู้ใช้ด้วย */
export interface CreateStallReclaimResult {
  created: CreateStallReclaimItem[];
  skipped: { leaseAgreementId: number; reason: string }[];
}

/** ผลของการพิจารณา — lease_canceled = true คือสัญญาถูกยกเลิกเรียบร้อยแล้ว */
export interface StallReclaimReviewResult {
  id_deposit_refund?: number;
  approved_amount?: DecimalString | null;
  lease_canceled?: boolean;
}

/**
 * axios 0.22 ประกาศ AxiosResponse<T = never> ถ้าไม่ใส่ generic res.data จะเป็น never
 * ทุกเมธอดจึงต้องระบุ type ของ data ให้ครบ
 */
export default class StallReclaimApi extends BaseAPI {
  static findAll(
    params: StallReclaimParams
  ): Promise<AxiosResponse<BaseResponse<Paginated<StallReclaimRow>>>> {
    return this.api.get(path, { params: { ...params } });
  }

  static summary(
    params: Omit<StallReclaimParams, "status" | "page" | "limit">
  ): Promise<AxiosResponse<BaseResponse<StallReclaimSummary>>> {
    return this.api.get(`${path}/summary`, { params: { ...params } });
  }

  static findById(id: number): Promise<AxiosResponse<BaseResponse<StallReclaimDetail>>> {
    return this.api.get(`${path}/${id}`);
  }

  static log(id: number): Promise<AxiosResponse<BaseResponse<StallReclaimLog[]>>> {
    return this.api.get(`${path}/${id}/log`);
  }

  static fileAll(id: number): Promise<AxiosResponse<BaseResponse<StallReclaimFile[]>>> {
    return this.api.get(`${path}/${id}/file`);
  }

  /** ช่อง "ผู้เช่าแผงค้า" — คืนเฉพาะคนที่มีสัญญาสถานะ ACTIVE */
  static ownerSearch(
    params: StallReclaimOwnerSearchParams
  ): Promise<AxiosResponse<BaseResponse<Paginated<StallReclaimOwner>>>> {
    return this.api.get(`${path}/owner/search`, { params: { ...params } });
  }

  /** สัญญาเช่าของผู้เช่าที่เลือก — userId ไม่ใช่ customerId */
  static ownerLease(
    userId: number,
    branchId?: number
  ): Promise<AxiosResponse<BaseResponse<StallReclaimLease[]>>> {
    return this.api.get(`${path}/owner/${userId}/lease`, { params: { branchId } });
  }

  /** ตอบ 201 พร้อม created[]/skipped[] — ต้องอ่าน skipped[] มาแจ้งผู้ใช้ */
  static create(
    body: CreateStallReclaimBody
  ): Promise<AxiosResponse<BaseResponse<CreateStallReclaimResult>>> {
    return this.api.post(path, body);
  }

  /**
   * multipart ยิงตรง ห้าม set Content-Type เอง ต้องให้ browser ใส่ boundary
   * body: result, note?, deduct_move_out?, deduct_damage?, refund_date?, doc_ref?, files[]
   * อนุมัติ = ระบบยกเลิกสัญญาเช่าให้ด้วย (ย้อนไม่ได้)
   */
  static review(
    id: number,
    body: FormData
  ): Promise<AxiosResponse<BaseResponse<StallReclaimReviewResult>>> {
    return this.api.post(`${path}/${id}/review`, body);
  }

  /** ลบคำสั่ง (soft delete) ได้เฉพาะ WAITING — ยังไม่มีปุ่มบนหน้าจอตามดีไซน์ */
  static remove(id: number): Promise<AxiosResponse<BaseResponse<boolean>>> {
    return this.api.delete(`${path}/${id}`);
  }

  /**
   * ดาวน์โหลดไฟล์แนบ — ผ่าน JWT guard จึงต้องยิงด้วย axios instance ไม่ใช่เปิด URL ตรง
   * responseType blob บังคับ ไม่งั้น axios parse เป็น string แล้วไฟล์ binary เสีย
   */
  static downloadFile(downloadUrl: string): Promise<AxiosResponse<Blob>> {
    return this.api.get(downloadUrl, { responseType: "blob" });
  }
}
